export type PhaseDatePart = number | null | undefined;

export interface PhaseDateTriple {
	year: PhaseDatePart;
	month: PhaseDatePart;
	day: PhaseDatePart;
}

export interface PhaseDateRange {
	start: PhaseDateTriple;
	end: PhaseDateTriple;
}

// Truthiness, not a null check, so the four consumers of a resolved range agree: a 0 in any part
// is never a valid year, month or day, and `Util.CreateNonUtcMomentDate` / `formatPhaseDate` both
// return null unless `year && month && day`. Treating 0 as populated would skip the Scoping
// fallback and then render an empty date, which is the blank this fallback exists to prevent.
const isCompleteTriple = (triple: PhaseDateTriple): boolean =>
	Boolean(triple.year) && Boolean(triple.month) && Boolean(triple.day);

/**
 * Picks the Baseline or Scoping source for a phase's date range.
 *
 * The range is resolved as a single unit: when unlinked, the Baseline range is used only if all six
 * of its parts are populated, otherwise the Scoping range is used whole. The six baseline date
 * columns are independently nullable -- service-company's `PhaseLogic.checkDatesSynch` mirrors start
 * and deadline in separate blocks, each gated on its own `isStartDaySet()` / `isDeadlineDaySet()`,
 * and skips mirroring entirely while a project is unlinked -- so a phase can hold a populated
 * baseline start next to a NULL baseline deadline. Falling back per-field, or even per-date, would
 * pair a Baseline start with a Scoping deadline: a range that exists in neither date set, and one
 * that can run backwards. An inverted range drives `handleAllocationTotalChange` to divide by a zero
 * working-day count, producing Infinity minutes per day in Convert Baseline to Placeholders.
 */
export const resolvePhaseDateRange = (
	useBaselineDates: boolean,
	baseline: PhaseDateRange,
	scoping: PhaseDateRange
): PhaseDateRange => {
	if (!useBaselineDates) {
		return scoping;
	}
	return isCompleteTriple(baseline.start) && isCompleteTriple(baseline.end) ? baseline : scoping;
};
